import { Box, Flex } from '@moonpig/launchpad-components'
import React, { FC } from 'react'
import { breakpointUp, styled } from '@moonpig/launchpad-utils'
import { Carousel } from '@moonpig/web-shared-components'
import { DepartmentsEnum, SortOrder } from '@moonpig/web-shared-types-graphql'
import { getParentDepartment } from '@moonpig/web-core-utils'
import { spacingPx } from '@moonpig/launchpad-theme'
import { system as s } from '@moonpig/launchpad-system'
import { useSearchStore } from '../../store/SearchStore'
import { DropDownMenu } from './DropDownMenu'
import { FilterCategory, IntegerFilter } from '../../services/types/services'
import { QuickSortByDropDown } from './QuickSortByDropDown/QuickSortByDropDown'
import { getSortByOptions } from '../../utils'
import { FiltersMenuButton } from '../FiltersCarousel/components'
import { ClearButton } from '../ClearButton'
import { FilterSource } from '../../types'
import { getSelectedIntegerFilters } from '../../store/helpers'
import { useFindLocaleText } from '../../text-localisation'

const StyledQuickFiltersBar = styled(Flex)`
  flex-direction: row;
  justify-content: space-between;
  gap: ${spacingPx(6)};
  width: 100%;
  max-width: 1200px;

  ${breakpointUp('md')} {
    overflow-x: scroll;
    overflow-y: hidden;
    overscroll-behavior: contain;
    scrollbar-width: none;
    -ms-overflow-style: none;
    &::-webkit-scrollbar {
      display: none;
    }
  }

  ${s({
    px: { md: 6, xl: 0 },
    marginX: 'auto',
  })}
`

const StyledCarousel = styled(Carousel)`
  width: 100%;
`

export const QuickFilters: FC<{ departments: DepartmentsEnum[] }> = ({
  departments,
}) => {
  const localiseText = useFindLocaleText()
  const selectedFilters = useSearchStore(store => store.selectedFilters)
  const toggleFiltersMenu = useSearchStore(store => store.toggleFiltersMenu)
  const filters = useSearchStore(store => store.filters)
  const quickFilterKeys = useSearchStore(store => store.quickFilterKeys)
  const toggleFilter = useSearchStore(store => store.toggleFilter)
  const filtersMenuOpen = useSearchStore(store => store.filtersMenuOpen)
  const resultsLoading = useSearchStore(store => store.loading)
  const results = useSearchStore(store => store.results)
  const toggleFiltersCategory = useSearchStore(
    store => store.toggleFiltersCategory,
  )
  const applyIntegerFilter = useSearchStore(store => store.applyIntegerFilter)
  const onIntegerFilterChange = useSearchStore(
    store => store.onIntegerFilterChange,
  )
  const integerFilters = useSearchStore(store => store.integerFilters)
  const clearFilters = useSearchStore(store => store.clearFilters)
  const sortValue = useSearchStore(store => store.sortValue)
  const sortBy = useSearchStore(store => store.sortBy)

  const department = getParentDepartment(departments[0])
  const isCards = department === DepartmentsEnum.ALL_CARDS
  const sortByOptions = getSortByOptions({ isCards, localiseText })
  const selectedIntegerFilters = getSelectedIntegerFilters({
    filters,
    integerFilters,
  })

  const selectedFiltersCombined = [
    ...selectedFilters,
    ...selectedIntegerFilters,
  ]

  const quickFilterCategories = quickFilterKeys.flatMap(key => {
    const filter = filters.find(
      (f): f is FilterCategory | IntegerFilter =>
        (f.__typename === 'FilterCategory' ||
          f.__typename === 'IntegerFilter') &&
        f.id === key,
    )

    return filter ? [filter] : []
  })

  return (
    <StyledQuickFiltersBar data-testid="dropdown-filters">
      <StyledCarousel
        itemsGap={{ xs: 0 }}
        $noSpacing
        alignItems="center"
        ariaLabel={localiseText('find.quick_filters')}
      >
        <FiltersMenuButton
          toggleMenu={toggleFiltersMenu}
          isMenuOpen={filtersMenuOpen}
          appliedFilters={selectedFiltersCombined}
        />
        {quickFilterCategories.map(filterCategory => {
          return (
            <DropDownMenu
              filterCategory={filterCategory}
              selectedFilters={selectedFilters}
              key={`${filterCategory.id}-quick-filter`}
              onChange={() => toggleFiltersCategory(filterCategory)}
              toggleFilter={toggleFilter}
              toggleFilterCategory={toggleFiltersCategory}
              onIntegerFilterChange={onIntegerFilterChange}
              integerFilters={integerFilters}
              applyIntegerFilter={applyIntegerFilter}
              count={results.totalCount}
              loading={resultsLoading}
              clearFilters={clearFilters}
            />
          )
        })}
        <Box
          display={{ xs: 'flex', md: 'flex', lg: 'none' }}
          alignItems="center"
          data-testid="mobile-quick-sort-by"
        >
          <QuickSortByDropDown
            sortOptions={sortByOptions}
            sortValue={sortValue}
            onSortBy={value => sortBy(value as SortOrder)}
            loading={resultsLoading}
          />
        </Box>
        <ClearButton
          level={0}
          source={FilterSource.QuickFilters}
          hide={selectedFiltersCombined.length === 0}
        />
      </StyledCarousel>
      <Box
        display={{ xs: 'none', lg: 'flex' }}
        alignItems="center"
        data-testid="desktop-quick-sort-by"
      >
        <QuickSortByDropDown
          sortOptions={sortByOptions}
          sortValue={sortValue}
          onSortBy={value => sortBy(value as SortOrder)}
          loading={resultsLoading}
        />
      </Box>
    </StyledQuickFiltersBar>
  )
}
